import { Prisma, PrismaClient } from "@prisma/client";

let insuranceInfoExtensions = {} as ReturnType<
  typeof getInsuranceInfoExtensions
>;

/** The client Student whose active plan this is. */
type StudentIdArg = { studentId: number };

/**
 * `guardianId` is the policy subscriber to write onto a newly created row,
 * never a lookup key.
 */
type ClientIds = StudentIdArg & { guardianId?: number };

type Args<A, S> = Prisma.Exact<A, Omit<S, "where">>;
type ArgsWithStudent<A, S> = StudentIdArg & Args<A, S>;
type ArgsWithClient<A, S> = ClientIds & Args<A, S>;

/** If replacing the current active, set the active end date to the current date */
const setOldActiveEnd = async (
  active: Prisma.InsuranceInfoGetPayload<{}> | null,
  p: PrismaClient,
) => {
  if (active) {
    await p.insuranceInfo.update({
      where: { id: active.id },
      data: {
        activeEndDate: new Date(),
      },
    });
  }
};

/**
 * Set the record as its student's active insurance info. The guardianId on a
 * student's plan is the policy subscriber, so only the student is connected.
 */
const updateClientConnections = async (
  insuranceInfo: Prisma.InsuranceInfoGetPayload<{}>,
  client: PrismaClient,
) => {
  if (insuranceInfo.studentId) {
    await client.student.update({
      where: { id: insuranceInfo.studentId },
      data: { activeInsuranceInfoId: insuranceInfo.id },
    });
  }
};
/** Given the current active insurance and update data, create an updated insurance info record */
const updateActiveHelper = async (
  args: Omit<Prisma.InsuranceInfoUpdateArgs, "where">,
  active: Prisma.InsuranceInfoGetPayload<{}> | null,
  client: PrismaClient,
) => {
  if (!active) {
    throw new Error("No active insurance info record found!");
  }

  let insuranceInfo = null;
  await client.$transaction(async (p) => {
    await setOldActiveEnd(active, p as PrismaClient);

    const {
      id: _,
      createdAt: __,
      updatedAt: ___,
      activeStartDate: _____,
      activeEndDate: ______,
      ...oldDataToKeep
    } = active;
    // Create a new insurance info record with the old data
    insuranceInfo = await (
      p.insuranceInfo as Prisma.InsuranceInfoDelegate
    ).create({
      data: {
        ...oldDataToKeep,
      },
    });
    // Then update it with the new data
    insuranceInfo = await (
      p.insuranceInfo as Prisma.InsuranceInfoDelegate
    ).update({
      where: { id: insuranceInfo.id },
      ...args,
    });

    // We now have a new active insurance info record, so set this as client's active insurance info
    await updateClientConnections(insuranceInfo, p as PrismaClient);
  });

  if (!insuranceInfo) {
    throw new Error("Insurance info unable to be updated!");
  }

  return insuranceInfo as Prisma.Result<
    Prisma.InsuranceInfoDelegate,
    Prisma.InsuranceInfoUpdateArgs,
    "update"
  >;
};

/** Given the current active insurance and create data, create a new insurance info record */
const createActiveHelper = async (
  args: Prisma.InsuranceInfoCreateArgs,
  active: Prisma.InsuranceInfoGetPayload<{}> | null,
  clients: ClientIds,
  client: PrismaClient,
) => {
  let insuranceInfo = null;
  await client.$transaction(async (p) => {
    await setOldActiveEnd(active, p as PrismaClient);

    const { data, ...restRemaining } = args;

    // Ensure that we're using the supplied student and/or guardian id
    // Check if the data is InsuranceInfoCreateInput vs InsuranceInfoUncheckedCreateInput
    // to determine how to connect the student and guardian
    const isInsuranceInfoUncheckedCreateInput = !(
      data?.Address ||
      data?.Guardian ||
      data?.Student ||
      data?.InsurancePayer
    );
    if (data && !(data.Student || data.studentId)) {
      if (isInsuranceInfoUncheckedCreateInput) {
        data.studentId = clients.studentId;
      } else {
        data.Student = { connect: { id: clients.studentId } };
      }
    }
    if (data && !(data.Guardian || data.guardianId) && clients.guardianId) {
      if (isInsuranceInfoUncheckedCreateInput) {
        data.guardianId = clients.guardianId;
      } else {
        data.Guardian = { connect: { id: clients.guardianId } };
      }
    }
    // Create a new insurance info record
    insuranceInfo = await (
      p.insuranceInfo as Prisma.InsuranceInfoDelegate
    ).create({
      data: {
        ...data,
        newPlanCounter:
          active?.newPlanCounter != null ? active.newPlanCounter + 1 : 0,
      },
      ...restRemaining,
    });

    // We now have a new active insurance info record, so set this as client's active insurance info
    await updateClientConnections(insuranceInfo, p as PrismaClient);
  });

  if (!insuranceInfo) {
    throw new Error("Insurance info unable to be created!");
  }

  return insuranceInfo as Prisma.Result<
    Prisma.InsuranceInfoDelegate,
    Prisma.InsuranceInfoCreateArgs,
    "create"
  >;
};

const getInsuranceInfoExtensions = () => {
  return Prisma.defineExtension((client) => {
    return client.$extends({
      model: {
        insuranceInfo: {
          async findActive<
            T extends Prisma.InsuranceInfoDelegate,
            A extends Omit<Prisma.InsuranceInfoFindFirstArgs, "where">,
          >(args: ArgsWithStudent<A, Prisma.InsuranceInfoFindFirstArgs>) {
            const { studentId, ...rest } = args as StudentIdArg & A;

            const info = await client.insuranceInfo.findFirst({
              where: { ActiveStudent: { id: studentId } },
              ...(rest as any),
            });

            return info as Prisma.Result<T, A, "findUnique">;
          },

          async updateActive<
            T extends Prisma.InsuranceInfoDelegate,
            A extends Omit<Prisma.InsuranceInfoUpdateArgs, "where">,
          >(args: ArgsWithStudent<A, Prisma.InsuranceInfoUpdateArgs>) {
            const { studentId, ...rest } = args as StudentIdArg & A;
            const active = await this.findActive({ studentId });

            if (!active) {
              throw new Error("No active insurance info record found!");
            }

            return (await updateActiveHelper(
              rest,
              active,
              client as PrismaClient,
            )) as Prisma.Result<T, A, "update">;
          },

          /**
           * Upsert an insurance info record and set it as the student's active insurance info.
           * Doesn't actually upsert - always creates a new record, but if `!createNewInsuranceInfo` and there is an active
           * insurance info record, it will use the supplied update data ontop of a copy of the active insurance info record.
           * @param args.createNewInsuranceInfo - Whether to create a new insurance info record even if one already exists
           */
          async upsertActive<
            T extends Prisma.InsuranceInfoDelegate,
            A extends Omit<Prisma.InsuranceInfoUpsertArgs, "where">,
          >(
            args: ArgsWithClient<A, Prisma.InsuranceInfoUpsertArgs> & {
              createNewInsuranceInfo?: boolean;
            },
          ) {
            const {
              studentId,
              guardianId,
              createNewInsuranceInfo = false,
              ...rest
            } = args as ClientIds & A & { createNewInsuranceInfo?: boolean };

            const active = await this.findActive({ studentId });

            let insuranceInfo = null;
            const {
              update: updateData,
              create: createData,
              ...restRemaining
            } = rest as Omit<Prisma.InsuranceInfoUpsertArgs, "where">;
            if (!active || createNewInsuranceInfo) {
              // ----Create a new insurance info record----
              insuranceInfo = await createActiveHelper(
                {
                  data: createData,
                  ...(restRemaining as any),
                },
                active,
                { studentId, guardianId },
                client as PrismaClient,
              );
            } else {
              // ----Update the active insurance info record----
              insuranceInfo = await updateActiveHelper(
                {
                  data: updateData,
                  ...restRemaining,
                },
                active,
                client as PrismaClient,
              );
            }

            if (!insuranceInfo) {
              throw new Error("Insurance info unable to be upserted!");
            }

            return insuranceInfo as Prisma.Result<T, A, "upsert">;
          },

          async createActive<
            T extends Prisma.InsuranceInfoDelegate,
            A extends Omit<Prisma.InsuranceInfoCreateArgs, "where">,
          >(args: ArgsWithClient<A, Prisma.InsuranceInfoCreateArgs>) {
            const { studentId, guardianId, ...rest } = args as ClientIds & A;
            const active = await this.findActive({ studentId });

            return (await createActiveHelper(
              rest,
              active,
              { studentId, guardianId },
              client as PrismaClient,
            )) as Prisma.Result<T, A, "create">;
          },
        },
      },
    });
  });
};

if (!process.browser) {
  insuranceInfoExtensions = getInsuranceInfoExtensions();
}

export default insuranceInfoExtensions;
